// Imports
import { useCallback } from 'react';
import { PaymentIntent, Stripe, StripeError } from '@stripe/stripe-js';
import { GetPaymentIntentResponse } from '../../../../../../../shareables/types/api/main/website/payment-option/payment-intent/get';
import useStripeInstance from '../../hooks/use-stripe-instance';
import useHandleStripeConfirmationError from '../../hooks/use-handle-stripe-confirmation-error';


// Define the result shape returned from the `confirm` callback
interface ConfirmResult {
	error?: StripeError;
	paymentIntent?: PaymentIntent;
}


// Define the accepted options
interface Options {
	stripeAccountID: string;
	confirm: (stripe: Stripe, clientSecret: string) => Promise<ConfirmResult>;
	onSuccess: () => void;
}


// A hook that manages the shared Stripe confirmation scaffolding for a stored payment method
const useStoredPaymentMethodSubmission = ({ stripeAccountID, confirm, onSuccess }: Options) => {
	// Use hooks
	const stripePromise = useStripeInstance(stripeAccountID);
	const handleStripeConfirmationError = useHandleStripeConfirmationError();
	
	
	// Handle submit
	const handleSubmit = useCallback(
		async (clientSecret: GetPaymentIntentResponse) => {
			// Resolve Stripe instance
			const stripe = await stripePromise;
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Delegate the Stripe confirmation step to the caller
			const { error, paymentIntent } = await confirm(stripe, clientSecret);
			
			
			// Handle error
			if (error) {
				handleStripeConfirmationError(error, { confirmedWithPaymentElement: false });
				return;
			}
			
			
			// Handle success or unexpected result
			if (paymentIntent) {
				onSuccess();
			} else {
				throw new Error('Unexpected missing payment intent');
			}
		},
		[stripePromise, confirm, onSuccess, handleStripeConfirmationError]
	);
	
	
	// Return
	return { handleSubmit };
};

export default useStoredPaymentMethodSubmission;
